/**
 * Small helper around the Web Speech API (SpeechSynthesis) for one-off spoken
 * phrases triggered by user actions (logo click, back-to-top, etc.).
 *
 * Because these are fired from real user gestures, browser autoplay policies
 * allow them to play immediately.
 */

// Pick the best available voice for a given BCP-47 language tag.
const pickVoice = (voices, langTag) => {
  if (!voices || voices.length === 0) return null;
  const base = langTag.split('-')[0].toLowerCase();
  return (
    voices.find((v) => v.lang?.toLowerCase() === langTag.toLowerCase()) ||
    voices.find((v) => v.lang?.toLowerCase().startsWith(base)) ||
    null
  );
};

/**
 * Speak a phrase out loud.
 * @param {string} text - The phrase to speak.
 * @param {string} [lang='en-IN'] - BCP-47 language tag for pronunciation.
 */
export const speak = (text, lang = 'en-IN') => {
  if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;
  if (!text) return;

  const synth = window.speechSynthesis;

  const run = () => {
    const utterance = new window.SpeechSynthesisUtterance(text);
    utterance.lang = lang;
    utterance.rate = 0.95;
    utterance.pitch = 1;
    utterance.volume = 1;

    const voice = pickVoice(synth.getVoices(), lang);
    if (voice) utterance.voice = voice;

    synth.cancel(); // stop anything currently playing so phrases don't overlap
    synth.resume(); // work around Chrome's paused-engine quirk
    synth.speak(utterance);
  };

  // Voices may not be loaded on the very first call — wait for them if needed.
  if (synth.getVoices().length === 0) {
    synth.addEventListener('voiceschanged', run, { once: true });
  } else {
    run();
  }
};

// Fixed patriotic farmer slogan — always spoken in Hindi.
export const SLOGAN = { text: 'जय जवान, जय किसान', lang: 'hi-IN' };

// "Save humanity & environment" message, localized per app language.
export const MISSION_MESSAGES = {
  en: {
    lang: 'en-IN',
    text:
      'With CropShield, we protect every crop, safeguard humanity, and nurture our environment for a greener tomorrow.',
  },
  hi: {
    lang: 'hi-IN',
    text:
      'क्रॉपशील्ड के साथ, हम हर फसल की रक्षा करते हैं, मानवता को सुरक्षित रखते हैं और हरित भविष्य के लिए पर्यावरण को बचाते हैं।',
  },
  gu: {
    lang: 'gu-IN',
    text:
      'ક્રૉપશીલ્ડ સાથે, અમે દરેક પાકનું રક્ષણ કરીએ છીએ, માનવતાને સુરક્ષિત રાખીએ છીએ અને હરિયાળા ભવિષ્ય માટે પર્યાવરણને બચાવીએ છીએ.',
  },
};
